import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import Toggle from 'react-toggle';
import { FaUndo } from 'react-icons/fa';
import momentPropTypes from 'react-moment-proptypes';
import classnames from 'classnames';
import isEqual from 'lodash-es/isEqual';
import intlShape from '@/shared/utils/intlShape';
import RelationSearch from '@/shared/components/input/RelationSearch';
import messages from '../interactionMessages';
import UserSearch from '@/shared/components/input/UserSearch';

import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import TextSearch from '@/shared/components/input/TextSearch';
import InteractionTagMultiSelect from '../InteractionTagMultiSelect';
import InteractionTypeButtonGroup from '../InteractionTypeButtonGroup';
import { FormattedMessage } from 'react-intl';
import PeriodButtonGroup, { getPeriodNameFromDates } from '@/shared/components/PeriodButtonGroup';
import { CanRead } from '@/shared/components/security';
import { functionalities } from '@/shared/services/AuthService';
import HasMultiCompany from '@/shared/components/security/HasMultiCompany';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';

export const defaultFilters = {
  relationId: null,
  relationName: null,
  createdBy: null,
  showLinkedRelationInteractions: true,
  searchValue: null,
  startDate: null,
  endDate: null,
  interactionTypes: [],
  period: 'all',
  tags: [],
  ownCompanies: [],
};
const interactionPeriods = ['month', 'months-6', 'all'];

const InteractionOverviewFilters = ({ filters, onFilterChange, intl, hasLinkedRelations }) => {
  const filterChanged = useCallback(
    filter => {
      const newFilters = { ...filters, ...filter };

      if (!isEqual(filters, newFilters)) {
        onFilterChange(newFilters);
      }
    },
    [filters, onFilterChange],
  );

  const initRelation = useMemo(
    () =>
      filters?.relationId
        ? {
            key: filters.relationId,
            label: filters.relationName,
          }
        : null,
    [filters?.relationId],
  );

  const hasFilters = !isEqual(filters, defaultFilters);
  return (
    <div className="info-block container-fluid">
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <TextSearch
              value={filters.searchValue}
              className="form-control"
              placeholder={intl.formatMessage(messages.Search)}
              onChange={value => filterChanged({ searchValue: value })}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <RelationSearch
              onChange={rel =>
                filterChanged({
                  relationId: rel?.key ?? null,
                  relationName: rel?.label ?? null,
                  showLinkedRelationInteractions: false,
                })
              }
              initialSelectedItem={initRelation}
              changeOnInit
            />
          </div>
        </div>
        {hasLinkedRelations && (
          <div className="col-md-4">
            <label className="react-toggle-wrapper margin5T" style={{ marginRight: '30px' }}>
              <Toggle
                checked={filters.showLinkedRelationInteractions}
                onChange={e =>
                  filterChanged({ showLinkedRelationInteractions: !!e.target.checked })
                }
              />
              <FormattedMessage
                className="label-text"
                {...messages.ShowInteractionsOfLinkedRelations}
              />
            </label>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-md-6">
          <DelayedDateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onDatesChange={({ startDate, endDate }) =>
              filterChanged({
                startDate,
                endDate,
                period: getPeriodNameFromDates(startDate, endDate, interactionPeriods),
              })
            }
            block
          />
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <PeriodButtonGroup
              dateFrom={filters.startDate}
              dateUntil={filters.endDate}
              period={filters.period}
              visiblePeriods={interactionPeriods}
              onChange={({ dateFrom, dateUntil, period }) =>
                filterChanged({ startDate: dateFrom, endDate: dateUntil, period })
              }
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <InteractionTypeButtonGroup
              value={filters.interactionTypes}
              onChange={value => filterChanged({ interactionTypes: value })}
              intl={intl}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <CanRead on={functionalities.InteractionsOthers}>
          <div className="col-md-4">
            <div className="form-group">
              <UserSearch
                onChange={user => filterChanged({ createdBy: user ? user.id : null })}
                value={filters.createdBy}
                placeholder={intl.formatMessage(messages.CreatedBy)}
              />
            </div>
          </div>
        </CanRead>
        <div className="col-md-4">
          <div className="form-group">
            <InteractionTagMultiSelect
              value={filters.tags}
              onChange={tags => filterChanged({ tags })}
              placeholder={intl.formatMessage(messages.Tags)}
            />
          </div>
        </div>
        <HasMultiCompany>
          <div className="col-md-4">
            <div className="form-group">
              <CompanyMultiSelect
                selection={filters.ownCompanies || []}
                onChange={companies => filterChanged({ ownCompanies: companies })}
              />
            </div>
          </div>
        </HasMultiCompany>
      </div>

      <div className="row">
        {hasFilters && (
          <div className="col-md-4">
            <button
              type="button"
              className="btn btn-default btn-reset"
              onClick={() => onFilterChange(defaultFilters)}
            >
              <FaUndo className="react-icon" />
              &nbsp;
              <FormattedMessage {...messages.ResetFilters} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
InteractionOverviewFilters.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    startDate: momentPropTypes.momentObj,
    endDate: momentPropTypes.momentObj,
    period: PropTypes.oneOf(interactionPeriods),
    search: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  hasLinkedRelations: PropTypes.bool,
  intl: intlShape.isRequired,
};
InteractionOverviewFilters.defaultProps = {
  filters: {
    startDate: null,
    endDate: null,
    period: null,
    interactionId: null,
    search: null,
    tags: null,
  },
  hasLinkedRelations: false,
};
export default InteractionOverviewFilters;
